/* =============================================================
   USA Language Schools — United Dream Mongolia
   96 English language schools across 8 cities for F-1 visa students
   Default sort: price ascending (cheapest first)
   ============================================================= */

export type SchoolType = "community_college" | "language_school" | "university_eli";

export interface USASchool {
  id: number;
  name: string;
  city: "Chicago" | "Los Angeles" | "New York" | "Boston" | "Washington DC" | "Seattle" | "Denver" | "Kansas City";
  cityMn: string;
  address: string;
  programs: string[];
  tuitionPerWeek: number;   // USD per week
  tuitionPerMonth: number;  // USD per month (average)
  minDurationWeeks: number; // minimum study duration in weeks
  accreditation: string;
  highlights: string[];
  website: string;
  rating: number; // out of 5
  schoolType: SchoolType;
  hasScholarship: boolean;   // scholarship available
  noEnglishTest: boolean;    // no IELTS/TOEFL required
}

export const USA_SCHOOLS: USASchool[] = [

  /* ══════════════════════════════════════════════════════════
     CHICAGO — 12 schools
  ══════════════════════════════════════════════════════════ */
  {
    id: 1,
    name: "City Colleges of Chicago — ESL Program",
    city: "Chicago", cityMn: "Чикаго",
    address: "226 W Jackson Blvd, Chicago, IL 60606",
    programs: ["ESL Intensive", "Academic English", "Vocational English", "GED Prep"],
    tuitionPerWeek: 120, tuitionPerMonth: 480,
    minDurationWeeks: 24,
    accreditation: "HLC, ACCET",
    highlights: ["Хамгийн хямд Чикагод", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Их сургуульд шилжих боломж"],
    website: "ccc.edu",
    rating: 4.3,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 2,
    name: "Midwestern Career College",
    city: "Chicago", cityMn: "Чикаго",
    address: "20 S Clark St Suite 200, Chicago, IL 60603",
    programs: ["ESL Foundation", "Business English", "Healthcare English", "IT English"],
    tuitionPerWeek: 150, tuitionPerMonth: 580,
    minDurationWeeks: 24,
    accreditation: "ACCET",
    highlights: ["Мэргэжлийн чиглэлтэй", "IELTS шаардахгүй", "Уян хатан хуваарь", "Хотын төвд"],
    website: "midwesterncc.edu",
    rating: 4.2,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 3,
    name: "Language Systems International Chicago",
    city: "Chicago", cityMn: "Чикаго",
    address: "30 W Monroe St, Chicago, IL 60603",
    programs: ["General English", "Intensive English", "Business English", "TOEFL Prep"],
    tuitionPerWeek: 310, tuitionPerMonth: 1180,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Хямд үнэ", "Уян хатан хуваарь", "Олон улсын орчин", "IELTS шаардахгүй"],
    website: "lsi.edu",
    rating: 4.2,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 4,
    name: "Intrax English Institute Chicago",
    city: "Chicago", cityMn: "Чикаго",
    address: "225 N Michigan Ave, Chicago, IL 60601",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "Conversation & Culture"],
    tuitionPerWeek: 350, tuitionPerMonth: 1320,
    minDurationWeeks: 2,
    accreditation: "ACCET",
    highlights: ["Богино хугацааны хөтөлбөр (2 долоо хоног)", "Нийгэмлэгийн үйл ажиллагаа", "IELTS шаардахгүй"],
    website: "intraxenglish.com",
    rating: 4.3,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 5,
    name: "EC English Chicago",
    city: "Chicago", cityMn: "Чикаго",
    address: "180 N Michigan Ave, Chicago, IL 60601",
    programs: ["Intensive English", "Business English", "TOEFL Prep", "Academic English"],
    tuitionPerWeek: 390, tuitionPerMonth: 1480,
    minDurationWeeks: 4,
    accreditation: "ACCET, CEA",
    highlights: ["Хотын төвд байрлал", "Олон улсын оюутнуудтай", "IELTS шаардахгүй"],
    website: "ecenglish.com",
    rating: 4.6,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 6,
    name: "Kaplan International Chicago",
    city: "Chicago", cityMn: "Чикаго",
    address: "55 E Monroe St, Chicago, IL 60603",
    programs: ["General English", "IELTS Preparation", "University Pathway", "Internship English"],
    tuitionPerWeek: 420, tuitionPerMonth: 1560,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Дэлхийд алдартай брэнд", "Их сургуулийн pathway хөтөлбөр", "Career дэмжлэг"],
    website: "kaplaninternational.com",
    rating: 4.5,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 7,
    name: "ELS Language Centers Chicago",
    city: "Chicago", cityMn: "Чикаго",
    address: "1 N Dearborn St, Chicago, IL 60602",
    programs: ["Intensive English Program", "Semi-Intensive", "TOEFL/IELTS Prep", "Business Communication"],
    tuitionPerWeek: 375, tuitionPerMonth: 1420,
    minDurationWeeks: 4,
    accreditation: "ACCET, CEA",
    highlights: ["75+ жилийн туршлага", "Их сургуульд шилжих боломж", "Дотуур байр байдаг"],
    website: "els.edu",
    rating: 4.4,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 8,
    name: "DePaul University English Language Academy",
    city: "Chicago", cityMn: "Чикаго",
    address: "1 E Jackson Blvd, Chicago, IL 60604",
    programs: ["Academic English", "University Prep", "IELTS/TOEFL", "Professional English"],
    tuitionPerWeek: 445, tuitionPerMonth: 1680,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["Их сургуулийн кампус", "Номын сан, спорт байгууламж", "Тэтгэлэг боломжтой"],
    website: "depaul.edu/ela",
    rating: 4.7,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 9,
    name: "Loyola University Chicago — ELP",
    city: "Chicago", cityMn: "Чикаго",
    address: "1032 W Sheridan Rd, Chicago, IL 60660",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "Graduate Prep"],
    tuitionPerWeek: 460, tuitionPerMonth: 1740,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["Jesuit их сургууль", "Тэтгэлэг боломжтой", "Нуурын эрэгт байрлал"],
    website: "luc.edu/elp",
    rating: 4.6,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 10,
    name: "Northwestern University — English Language Program",
    city: "Chicago", cityMn: "Чикаго",
    address: "633 Clark St, Evanston, IL 60208",
    programs: ["Academic English", "Research Writing", "TOEFL Prep", "Graduate Academic Skills"],
    tuitionPerWeek: 580, tuitionPerMonth: 2200,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["Top 10 их сургууль", "Судалгааны орчин", "Тэтгэлэг боломжтой"],
    website: "northwestern.edu/elp",
    rating: 4.9,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: false,
  },
  {
    id: 11,
    name: "Illinois Institute of Technology — ELP",
    city: "Chicago", cityMn: "Чикаго",
    address: "3300 S Federal St, Chicago, IL 60616",
    programs: ["Academic English", "STEM English", "University Prep", "TOEFL/IELTS"],
    tuitionPerWeek: 490, tuitionPerMonth: 1860,
    minDurationWeeks: 24,
    accreditation: "CEA",
    highlights: ["Технологийн их сургууль", "STEM чиглэл", "Тэтгэлэг боломжтой"],
    website: "iit.edu/elp",
    rating: 4.5,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 12,
    name: "Moraine Valley Community College — ESL",
    city: "Chicago", cityMn: "Чикаго",
    address: "9000 W College Pkwy, Palos Hills, IL 60465",
    programs: ["ESL Intensive", "Vocational English", "Academic Preparation", "Conversation"],
    tuitionPerWeek: 140, tuitionPerMonth: 530,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Маш хямд", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Тайван орчин"],
    website: "morainevalley.edu",
    rating: 4.1,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },

  /* ══════════════════════════════════════════════════════════
     LOS ANGELES — 12 schools
  ══════════════════════════════════════════════════════════ */
  {
    id: 13,
    name: "Santa Monica College — ESL Program",
    city: "Los Angeles", cityMn: "Лос-Анжелес",
    address: "1900 Pico Blvd, Santa Monica, CA 90405",
    programs: ["ESL Intensive", "Academic English", "Conversation", "TOEFL Prep"],
    tuitionPerWeek: 130, tuitionPerMonth: 500,
    minDurationWeeks: 24,
    accreditation: "ACCJC",
    highlights: ["UCLA-д шилжих баталгаа", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Далайн эрэгт ойр"],
    website: "smc.edu",
    rating: 4.5,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 14,
    name: "Los Angeles City College — ESL",
    city: "Los Angeles", cityMn: "Лос-Анжелес",
    address: "855 N Vermont Ave, Los Angeles, CA 90029",
    programs: ["ESL Basic", "ESL Intensive", "Academic Preparation", "Vocational English"],
    tuitionPerWeek: 110, tuitionPerMonth: 440,
    minDurationWeeks: 24,
    accreditation: "ACCJC",
    highlights: ["Хамгийн хямд LA-д", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Олон улсын орчин"],
    website: "lacitycollege.edu",
    rating: 4.1,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 15,
    name: "LASC — Los Angeles School of English",
    city: "Los Angeles", cityMn: "Лос-Анжелес",
    address: "3550 Wilshire Blvd, Los Angeles, CA 90010",
    programs: ["Intensive English", "Conversation", "TOEFL Prep", "Academic Preparation"],
    tuitionPerWeek: 320, tuitionPerMonth: 1220,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Хямд үнэ", "Уян хатан цагийн хуваарь", "Homestay байрлал", "IELTS шаардахгүй"],
    website: "lasc.edu",
    rating: 4.3,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 16,
    name: "Westwood College of English",
    city: "Los Angeles", cityMn: "Лос-Анжелес",
    address: "10880 Wilshire Blvd, Los Angeles, CA 90024",
    programs: ["General English", "Business English", "Academic English", "IELTS/TOEFL"],
    tuitionPerWeek: 360, tuitionPerMonth: 1360,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Westwood-д байрлал", "UCLA-ийн ойролцоо", "IELTS шаардахгүй"],
    website: "westwoodcollege.edu",
    rating: 4.4,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 17,
    name: "EC English Los Angeles",
    city: "Los Angeles", cityMn: "Лос-Анжелес",
    address: "6420 Wilshire Blvd, Los Angeles, CA 90048",
    programs: ["Intensive English", "Business English", "TOEFL/IELTS", "Academic Year"],
    tuitionPerWeek: 410, tuitionPerMonth: 1540,
    minDurationWeeks: 4,
    accreditation: "ACCET, CEA",
    highlights: ["Голливудын ойролцоо", "Нарлаг уур амьсгал", "IELTS шаардахгүй"],
    website: "ecenglish.com/la",
    rating: 4.6,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 18,
    name: "ELS Language Centers Los Angeles",
    city: "Los Angeles", cityMn: "Лос-Анжелес",
    address: "3580 Wilshire Blvd, Los Angeles, CA 90010",
    programs: ["Intensive English", "Semi-Intensive", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 385, tuitionPerMonth: 1460,
    minDurationWeeks: 4,
    accreditation: "ACCET, CEA",
    highlights: ["Их сургуульд шилжих баталгаа", "Дотуур байр байдаг", "IELTS шаардахгүй"],
    website: "els.edu/la",
    rating: 4.5,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 19,
    name: "Kaplan International Los Angeles",
    city: "Los Angeles", cityMn: "Лос-Анжелес",
    address: "1800 Century Park E, Los Angeles, CA 90067",
    programs: ["General English", "IELTS Prep", "University Pathway", "Internship Program"],
    tuitionPerWeek: 435, tuitionPerMonth: 1640,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Century City-д байрлал", "Олон улсын оюутнууд", "Ажлын туршлага хөтөлбөр"],
    website: "kaplaninternational.com/la",
    rating: 4.5,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 20,
    name: "Cal State LA — English Language Program",
    city: "Los Angeles", cityMn: "Лос-Анжелес",
    address: "5151 State University Dr, Los Angeles, CA 90032",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 420, tuitionPerMonth: 1580,
    minDurationWeeks: 24,
    accreditation: "CEA, WASC",
    highlights: ["Их сургуулийн кампус", "Тэтгэлэг боломжтой", "Cal State-д шилжих боломж"],
    website: "calstatela.edu/elp",
    rating: 4.4,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 21,
    name: "USC — American Language Institute",
    city: "Los Angeles", cityMn: "Лос-Анжелес",
    address: "3601 Watt Way, Los Angeles, CA 90089",
    programs: ["Intensive English", "Academic English", "TOEFL Prep", "Graduate Prep"],
    tuitionPerWeek: 560, tuitionPerMonth: 2120,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["Top их сургууль", "Тэтгэлэг боломжтой", "Hollywood ойролцоо"],
    website: "ali.usc.edu",
    rating: 4.8,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: false,
  },
  {
    id: 22,
    name: "UCLA Extension English Language Center",
    city: "Los Angeles", cityMn: "Лос-Анжелес",
    address: "10995 Le Conte Ave, Los Angeles, CA 90024",
    programs: ["Academic English", "TOEFL Prep", "Business Communication", "Writing Skills"],
    tuitionPerWeek: 520, tuitionPerMonth: 1960,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["UCLA кампус дотор", "Дэлхийд алдартай", "Тэтгэлэг боломжтой"],
    website: "uclaextension.edu",
    rating: 4.8,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: false,
  },
  {
    id: 23,
    name: "Pasadena City College — ESL",
    city: "Los Angeles", cityMn: "Лос-Анжелес",
    address: "1570 E Colorado Blvd, Pasadena, CA 91106",
    programs: ["ESL Intensive", "Academic English", "Conversation", "Vocational English"],
    tuitionPerWeek: 125, tuitionPerMonth: 480,
    minDurationWeeks: 24,
    accreditation: "ACCJC",
    highlights: ["Хямд үнэ", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Тайван хот"],
    website: "pasadena.edu",
    rating: 4.3,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 24,
    name: "Glendale Community College — ESL",
    city: "Los Angeles", cityMn: "Лос-Анжелес",
    address: "1500 N Verdugo Rd, Glendale, CA 91208",
    programs: ["ESL Intensive", "Academic Preparation", "Business English", "TOEFL Prep"],
    tuitionPerWeek: 115, tuitionPerMonth: 450,
    minDurationWeeks: 24,
    accreditation: "ACCJC",
    highlights: ["Маш хямд", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Аюулгүй хот"],
    website: "glendale.edu",
    rating: 4.2,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },

  /* ══════════════════════════════════════════════════════════
     NEW YORK — 12 schools
  ══════════════════════════════════════════════════════════ */
  {
    id: 25,
    name: "CUNY — City University of New York ESL",
    city: "New York", cityMn: "Нью-Йорк",
    address: "535 E 80th St, New York, NY 10075",
    programs: ["ESL Intensive", "Academic English", "Conversation", "TOEFL Prep"],
    tuitionPerWeek: 145, tuitionPerMonth: 560,
    minDurationWeeks: 24,
    accreditation: "MSCHE",
    highlights: ["Нью-Йоркт хамгийн хямд", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "12 кампус"],
    website: "cuny.edu/esl",
    rating: 4.3,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 26,
    name: "Borough of Manhattan Community College — ESL",
    city: "New York", cityMn: "Нью-Йорк",
    address: "199 Chambers St, New York, NY 10007",
    programs: ["ESL Foundation", "Academic English", "Business English", "Vocational English"],
    tuitionPerWeek: 155, tuitionPerMonth: 590,
    minDurationWeeks: 24,
    accreditation: "MSCHE",
    highlights: ["Манхэттэнд байрлал", "Тэтгэлэг боломжтой", "IELTS шаардахгүй"],
    website: "bmcc.cuny.edu",
    rating: 4.2,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 27,
    name: "ILSC New York",
    city: "New York", cityMn: "Нью-Йорк",
    address: "5 Hanover Sq, New York, NY 10004",
    programs: ["General English", "Intensive English", "Business English", "TOEFL/IELTS Prep"],
    tuitionPerWeek: 340, tuitionPerMonth: 1280,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Уолл Стрийтийн ойролцоо", "IELTS шаардахгүй", "Олон улсын орчин"],
    website: "ilsc.com/new-york",
    rating: 4.4,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 28,
    name: "EF International Language Campus New York",
    city: "New York", cityMn: "Нью-Йорк",
    address: "226 W 26th St, New York, NY 10001",
    programs: ["General English", "Intensive English", "Business English", "TOEFL Prep"],
    tuitionPerWeek: 380, tuitionPerMonth: 1440,
    minDurationWeeks: 2,
    accreditation: "ACCET",
    highlights: ["Богино хугацааны хөтөлбөр (2 долоо хоног)", "Chelsea-д байрлал", "IELTS шаардахгүй"],
    website: "ef.com/ny",
    rating: 4.5,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 29,
    name: "Kaplan International New York",
    city: "New York", cityMn: "Нью-Йорк",
    address: "131 W 56th St, New York, NY 10019",
    programs: ["General English", "IELTS Prep", "University Pathway", "Business English"],
    tuitionPerWeek: 450, tuitionPerMonth: 1700,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Midtown Манхэттэн", "Их сургуульд шилжих", "Career дэмжлэг"],
    website: "kaplaninternational.com/ny",
    rating: 4.5,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 30,
    name: "EC English New York",
    city: "New York", cityMn: "Нью-Йорк",
    address: "1 Rockefeller Plaza, New York, NY 10020",
    programs: ["Intensive English", "Business English", "TOEFL/IELTS", "Academic Year"],
    tuitionPerWeek: 420, tuitionPerMonth: 1580,
    minDurationWeeks: 4,
    accreditation: "ACCET, CEA",
    highlights: ["Rockefeller Center-д", "Нью-Йоркийн зүрхэнд", "IELTS шаардахгүй"],
    website: "ecenglish.com/ny",
    rating: 4.6,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 31,
    name: "Embassy English New York",
    city: "New York", cityMn: "Нью-Йорк",
    address: "330 7th Ave, New York, NY 10001",
    programs: ["General English", "Intensive English", "IELTS Prep", "Business English"],
    tuitionPerWeek: 360, tuitionPerMonth: 1360,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Хямд үнэ", "IELTS шаардахгүй", "Уян хатан хуваарь"],
    website: "embassyenglish.com/ny",
    rating: 4.3,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 32,
    name: "Columbia University — American Language Program",
    city: "New York", cityMn: "Нью-Йорк",
    address: "2970 Broadway, New York, NY 10027",
    programs: ["Intensive English", "Academic Writing", "TOEFL Prep", "Graduate Prep"],
    tuitionPerWeek: 620, tuitionPerMonth: 2340,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["Ivy League их сургууль", "Тэтгэлэг боломжтой", "Дэлхийн шилдэг"],
    website: "alp.columbia.edu",
    rating: 4.9,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: false,
  },
  {
    id: 33,
    name: "NYU — English Language Institute",
    city: "New York", cityMn: "Нью-Йорк",
    address: "7 E 12th St, New York, NY 10003",
    programs: ["Intensive English", "Academic English", "Business English", "TOEFL Prep"],
    tuitionPerWeek: 580, tuitionPerMonth: 2200,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["NYU кампус", "Тэтгэлэг боломжтой", "Greenwich Village-д"],
    website: "nyu.edu/eli",
    rating: 4.8,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: false,
  },
  {
    id: 34,
    name: "Fordham University — ELP",
    city: "New York", cityMn: "Нью-Йорк",
    address: "441 E Fordham Rd, Bronx, NY 10458",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 490, tuitionPerMonth: 1860,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["Jesuit их сургууль", "Тэтгэлэг боломжтой", "Fordham-д шилжих боломж"],
    website: "fordham.edu/elp",
    rating: 4.6,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 35,
    name: "New York Film Academy — ESL",
    city: "New York", cityMn: "Нью-Йорк",
    address: "17 Battery Pl, New York, NY 10004",
    programs: ["ESL + Film", "ESL + Acting", "English for Media", "General English"],
    tuitionPerWeek: 400, tuitionPerMonth: 1520,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Кино, урлагтай хослуулсан", "Өвөрмөц хөтөлбөр", "IELTS шаардахгүй"],
    website: "nyfa.edu/esl",
    rating: 4.4,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 36,
    name: "Queensborough Community College — ESL",
    city: "New York", cityMn: "Нью-Йорк",
    address: "222-05 56th Ave, Bayside, NY 11364",
    programs: ["ESL Intensive", "Academic English", "Vocational English", "Conversation"],
    tuitionPerWeek: 130, tuitionPerMonth: 500,
    minDurationWeeks: 24,
    accreditation: "MSCHE",
    highlights: ["Хямд үнэ", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Тайван хот"],
    website: "qcc.cuny.edu",
    rating: 4.1,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },

  /* ══════════════════════════════════════════════════════════
     BOSTON — 12 schools
  ══════════════════════════════════════════════════════════ */
  {
    id: 37,
    name: "Bunker Hill Community College — ESL",
    city: "Boston", cityMn: "Бостон",
    address: "250 New Rutherford Ave, Boston, MA 02129",
    programs: ["ESL Intensive", "Academic English", "Vocational English", "Conversation"],
    tuitionPerWeek: 135, tuitionPerMonth: 520,
    minDurationWeeks: 24,
    accreditation: "NECHE",
    highlights: ["Бостонд хамгийн хямд", "Тэтгэлэг боломжтой", "IELTS шаардахгүй"],
    website: "bhcc.edu",
    rating: 4.2,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 38,
    name: "Massasoit Community College — ESL",
    city: "Boston", cityMn: "Бостон",
    address: "1 Massasoit Blvd, Brockton, MA 02302",
    programs: ["ESL Foundation", "ESL Intensive", "Academic Preparation", "Vocational English"],
    tuitionPerWeek: 120, tuitionPerMonth: 460,
    minDurationWeeks: 24,
    accreditation: "NECHE",
    highlights: ["Хамгийн хямд Бостон бүсэд", "Тэтгэлэг боломжтой", "IELTS шаардахгүй"],
    website: "massasoit.edu",
    rating: 4.0,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 39,
    name: "EF International Boston",
    city: "Boston", cityMn: "Бостон",
    address: "1 Education St, Cambridge, MA 02141",
    programs: ["General English", "Intensive English", "Business English", "TOEFL Prep"],
    tuitionPerWeek: 370, tuitionPerMonth: 1400,
    minDurationWeeks: 2,
    accreditation: "ACCET",
    highlights: ["Богино хугацааны хөтөлбөр (2 долоо хоног)", "Cambridge-д байрлал", "IELTS шаардахгүй"],
    website: "ef.com/boston",
    rating: 4.5,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 40,
    name: "Kaplan International Boston",
    city: "Boston", cityMn: "Бостон",
    address: "31 St James Ave, Boston, MA 02116",
    programs: ["General English", "IELTS Prep", "University Pathway", "Business English"],
    tuitionPerWeek: 430, tuitionPerMonth: 1620,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Хотын төвд", "Их сургуульд шилжих", "IELTS шаардахгүй"],
    website: "kaplaninternational.com/boston",
    rating: 4.5,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 41,
    name: "EC English Boston",
    city: "Boston", cityMn: "Бостон",
    address: "1 Faneuil Hall Marketplace, Boston, MA 02109",
    programs: ["Intensive English", "Business English", "TOEFL/IELTS", "Academic Year"],
    tuitionPerWeek: 400, tuitionPerMonth: 1520,
    minDurationWeeks: 4,
    accreditation: "ACCET, CEA",
    highlights: ["Faneuil Hall ойролцоо", "Түүхэн хотод", "IELTS шаардахгүй"],
    website: "ecenglish.com/boston",
    rating: 4.6,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 42,
    name: "Embassy English Boston",
    city: "Boston", cityMn: "Бостон",
    address: "10 Milk St, Boston, MA 02108",
    programs: ["General English", "Intensive English", "IELTS Prep", "Business English"],
    tuitionPerWeek: 350, tuitionPerMonth: 1320,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Хямд үнэ", "IELTS шаардахгүй", "Downtown Бостон"],
    website: "embassyenglish.com/boston",
    rating: 4.3,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 43,
    name: "Harvard Extension School — ALI",
    city: "Boston", cityMn: "Бостон",
    address: "51 Brattle St, Cambridge, MA 02138",
    programs: ["Academic English", "Research Writing", "TOEFL Prep", "Graduate Prep"],
    tuitionPerWeek: 640, tuitionPerMonth: 2420,
    minDurationWeeks: 24,
    accreditation: "NECHE, UCIEP",
    highlights: ["Harvard нэртэй", "Тэтгэлэг боломжтой", "Дэлхийн шилдэг"],
    website: "extension.harvard.edu",
    rating: 4.9,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: false,
  },
  {
    id: 44,
    name: "Boston University — CELOP",
    city: "Boston", cityMn: "Бостон",
    address: "890 Commonwealth Ave, Boston, MA 02215",
    programs: ["Intensive English", "Academic English", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 560, tuitionPerMonth: 2120,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["BU кампус дотор", "Тэтгэлэг боломжтой", "BU-д шилжих боломж"],
    website: "bu.edu/celop",
    rating: 4.8,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: false,
  },
  {
    id: 45,
    name: "Northeastern University — DMSB ELP",
    city: "Boston", cityMn: "Бостон",
    address: "360 Huntington Ave, Boston, MA 02115",
    programs: ["Intensive English", "Business English", "Co-op Prep", "TOEFL Prep"],
    tuitionPerWeek: 520, tuitionPerMonth: 1960,
    minDurationWeeks: 24,
    accreditation: "CEA, NECHE",
    highlights: ["Co-op хөтөлбөртэй", "Тэтгэлэг боломжтой", "Ажлын туршлага"],
    website: "northeastern.edu/elp",
    rating: 4.7,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 46,
    name: "Suffolk University — ELP",
    city: "Boston", cityMn: "Бостон",
    address: "8 Ashburton Pl, Boston, MA 02108",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "Business English"],
    tuitionPerWeek: 480, tuitionPerMonth: 1820,
    minDurationWeeks: 24,
    accreditation: "CEA, NECHE",
    highlights: ["Хотын төвд", "Тэтгэлэг боломжтой", "Suffolk-д шилжих боломж"],
    website: "suffolk.edu/elp",
    rating: 4.5,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 47,
    name: "Tufts University — ELP",
    city: "Boston", cityMn: "Бостон",
    address: "419 Boston Ave, Medford, MA 02155",
    programs: ["Academic English", "TOEFL Prep", "Research Writing", "Graduate Prep"],
    tuitionPerWeek: 540, tuitionPerMonth: 2040,
    minDurationWeeks: 24,
    accreditation: "CEA, NECHE",
    highlights: ["Top их сургууль", "Тэтгэлэг боломжтой", "Судалгааны орчин"],
    website: "tufts.edu/elp",
    rating: 4.7,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: false,
  },
  {
    id: 48,
    name: "Roxbury Community College — ESL",
    city: "Boston", cityMn: "Бостон",
    address: "1234 Columbus Ave, Boston, MA 02120",
    programs: ["ESL Intensive", "Academic English", "Vocational English", "Conversation"],
    tuitionPerWeek: 130, tuitionPerMonth: 500,
    minDurationWeeks: 24,
    accreditation: "NECHE",
    highlights: ["Хямд үнэ", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Олон улсын орчин"],
    website: "rcc.mass.edu",
    rating: 4.1,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },

  /* ══════════════════════════════════════════════════════════
     WASHINGTON DC — 12 schools
  ══════════════════════════════════════════════════════════ */
  {
    id: 49,
    name: "Northern Virginia Community College — ESL",
    city: "Washington DC", cityMn: "Вашингтон",
    address: "4001 Wakefield Chapel Rd, Annandale, VA 22003",
    programs: ["ESL Intensive", "Academic English", "Vocational English", "Conversation"],
    tuitionPerWeek: 125, tuitionPerMonth: 480,
    minDurationWeeks: 24,
    accreditation: "SACSCOC",
    highlights: ["DC бүсэд хамгийн хямд", "Тэтгэлэг боломжтой", "IELTS шаардахгүй"],
    website: "nvcc.edu",
    rating: 4.3,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 50,
    name: "Montgomery College — ESL",
    city: "Washington DC", cityMn: "Вашингтон",
    address: "900 Hungerford Dr, Rockville, MD 20850",
    programs: ["ESL Foundation", "ESL Intensive", "Academic Preparation", "Business English"],
    tuitionPerWeek: 130, tuitionPerMonth: 500,
    minDurationWeeks: 24,
    accreditation: "MSCHE",
    highlights: ["Хямд үнэ", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Maryland-д"],
    website: "montgomerycollege.edu",
    rating: 4.2,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 51,
    name: "Washington English Center",
    city: "Washington DC", cityMn: "Вашингтон",
    address: "3251 Prospect St NW, Washington, DC 20007",
    programs: ["General English", "Conversation", "Academic English", "TOEFL Prep"],
    tuitionPerWeek: 290, tuitionPerMonth: 1100,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Хамгийн хямд DC-д", "Georgetown ойролцоо", "IELTS шаардахгүй"],
    website: "washingtonenglish.org",
    rating: 4.2,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 52,
    name: "ELS Language Centers Washington DC",
    city: "Washington DC", cityMn: "Вашингтон",
    address: "1629 K St NW, Washington, DC 20006",
    programs: ["Intensive English", "TOEFL Prep", "Academic English", "Business Communication"],
    tuitionPerWeek: 400, tuitionPerMonth: 1520,
    minDurationWeeks: 4,
    accreditation: "ACCET, CEA",
    highlights: ["Засгийн газрын дүүрэгт", "Музей, түүхэн газруудтай ойр", "IELTS шаардахгүй"],
    website: "els.edu/dc",
    rating: 4.4,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 53,
    name: "Inlingua Washington DC",
    city: "Washington DC", cityMn: "Вашингтон",
    address: "1776 I St NW, Washington, DC 20006",
    programs: ["Intensive English", "Business English", "Diplomatic English", "TOEFL/IELTS"],
    tuitionPerWeek: 420, tuitionPerMonth: 1580,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Дипломат хэлний мэргэжилтнүүд", "Корпорат хөтөлбөр", "IELTS шаардахгүй"],
    website: "inlingua-dc.com",
    rating: 4.6,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 54,
    name: "Kaplan International Washington DC",
    city: "Washington DC", cityMn: "Вашингтон",
    address: "1025 Connecticut Ave NW, Washington, DC 20036",
    programs: ["General English", "IELTS Prep", "Business English", "University Pathway"],
    tuitionPerWeek: 445, tuitionPerMonth: 1680,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Хотын төвд байрлал", "Олон улсын оюутнууд", "IELTS шаардахгүй"],
    website: "kaplaninternational.com/dc",
    rating: 4.5,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 55,
    name: "Georgetown University — English Language Programs",
    city: "Washington DC", cityMn: "Вашингтон",
    address: "37th & O St NW, Washington, DC 20057",
    programs: ["Academic English", "Business English", "TOEFL Prep", "Professional Development"],
    tuitionPerWeek: 560, tuitionPerMonth: 2120,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["Дэлхийд алдартай", "Нийслэл хотод", "Тэтгэлэг боломжтой"],
    website: "scs.georgetown.edu",
    rating: 4.9,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: false,
  },
  {
    id: 56,
    name: "American University — Language Center",
    city: "Washington DC", cityMn: "Вашингтон",
    address: "4400 Massachusetts Ave NW, Washington, DC 20016",
    programs: ["Intensive English", "Academic Preparation", "IELTS/TOEFL", "Graduate Prep"],
    tuitionPerWeek: 530, tuitionPerMonth: 2000,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["Их сургуулийн кампус", "Тэтгэлэг боломжтой", "Нэр хүндтэй"],
    website: "american.edu/language",
    rating: 4.8,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: false,
  },
  {
    id: 57,
    name: "George Washington University — ELP",
    city: "Washington DC", cityMn: "Вашингтон",
    address: "2121 I St NW, Washington, DC 20052",
    programs: ["Intensive English", "Academic English", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 510, tuitionPerMonth: 1940,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["GWU кампус", "Тэтгэлэг боломжтой", "Засгийн газрын ойролцоо"],
    website: "gwu.edu/elp",
    rating: 4.7,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 58,
    name: "Howard University — ELP",
    city: "Washington DC", cityMn: "Вашингтон",
    address: "2400 6th St NW, Washington, DC 20059",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "Cultural English"],
    tuitionPerWeek: 470, tuitionPerMonth: 1780,
    minDurationWeeks: 24,
    accreditation: "MSCHE",
    highlights: ["Түүхэн их сургууль", "Тэтгэлэг боломжтой", "Олон улсын орчин"],
    website: "howard.edu/elp",
    rating: 4.5,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 59,
    name: "Catholic University — ELP",
    city: "Washington DC", cityMn: "Вашингтон",
    address: "620 Michigan Ave NE, Washington, DC 20064",
    programs: ["Intensive English", "Academic English", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 490, tuitionPerMonth: 1860,
    minDurationWeeks: 24,
    accreditation: "CEA, MSCHE",
    highlights: ["Католик их сургууль", "Тэтгэлэг боломжтой", "Тайван кампус"],
    website: "catholic.edu/elp",
    rating: 4.6,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 60,
    name: "Prince George's Community College — ESL",
    city: "Washington DC", cityMn: "Вашингтон",
    address: "301 Largo Rd, Largo, MD 20774",
    programs: ["ESL Intensive", "Academic English", "Vocational English", "Conversation"],
    tuitionPerWeek: 120, tuitionPerMonth: 460,
    minDurationWeeks: 24,
    accreditation: "MSCHE",
    highlights: ["Маш хямд", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Maryland-д"],
    website: "pgcc.edu",
    rating: 4.1,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },

  /* ══════════════════════════════════════════════════════════
     SEATTLE — 12 schools
  ══════════════════════════════════════════════════════════ */
  {
    id: 61,
    name: "Seattle Central College — ESL",
    city: "Seattle", cityMn: "Сиетл",
    address: "1701 Broadway, Seattle, WA 98122",
    programs: ["ESL Intensive", "Academic English", "Vocational English", "Conversation"],
    tuitionPerWeek: 130, tuitionPerMonth: 500,
    minDurationWeeks: 24,
    accreditation: "NWCCU",
    highlights: ["Сиетлд хамгийн хямд", "Тэтгэлэг боломжтой", "IELTS шаардахгүй"],
    website: "seattlecentral.edu",
    rating: 4.3,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 62,
    name: "Bellevue College — ESL",
    city: "Seattle", cityMn: "Сиетл",
    address: "3000 Landerholm Circle SE, Bellevue, WA 98007",
    programs: ["ESL Foundation", "ESL Intensive", "Academic Preparation", "Business English"],
    tuitionPerWeek: 140, tuitionPerMonth: 540,
    minDurationWeeks: 24,
    accreditation: "NWCCU",
    highlights: ["Microsoft-ийн ойролцоо", "Тэтгэлэг боломжтой", "IELTS шаардахгүй"],
    website: "bellevuecollege.edu",
    rating: 4.4,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 63,
    name: "Seattle Language Academy",
    city: "Seattle", cityMn: "Сиетл",
    address: "720 Olive Way, Seattle, WA 98101",
    programs: ["Intensive English", "Business English", "TOEFL/IELTS", "Conversation & Culture"],
    tuitionPerWeek: 330, tuitionPerMonth: 1250,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Хямд үнэ", "Уян хатан хуваарь", "IELTS шаардахгүй"],
    website: "seattlelanguageacademy.com",
    rating: 4.3,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 64,
    name: "ELS Language Centers Seattle",
    city: "Seattle", cityMn: "Сиетл",
    address: "1411 4th Ave, Seattle, WA 98101",
    programs: ["Intensive English", "Semi-Intensive", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 380, tuitionPerMonth: 1440,
    minDurationWeeks: 4,
    accreditation: "ACCET, CEA",
    highlights: ["Их сургуульд шилжих баталгаа", "Дотуур байр байдаг", "IELTS шаардахгүй"],
    website: "els.edu/seattle",
    rating: 4.4,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 65,
    name: "EC English Seattle",
    city: "Seattle", cityMn: "Сиетл",
    address: "1200 6th Ave, Seattle, WA 98101",
    programs: ["Intensive English", "Business English", "IELTS/TOEFL", "Academic Year"],
    tuitionPerWeek: 400, tuitionPerMonth: 1520,
    minDurationWeeks: 4,
    accreditation: "ACCET, CEA",
    highlights: ["Хотын төвд", "Технологийн компаниудтай ойр", "IELTS шаардахгүй"],
    website: "ecenglish.com/seattle",
    rating: 4.6,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 66,
    name: "Kaplan International Seattle",
    city: "Seattle", cityMn: "Сиетл",
    address: "1601 5th Ave, Seattle, WA 98101",
    programs: ["General English", "IELTS Prep", "University Pathway", "Tech English"],
    tuitionPerWeek: 415, tuitionPerMonth: 1560,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Amazon HQ ойролцоо", "Технологийн салбарт ажиллах боломж", "IELTS шаардахгүй"],
    website: "kaplaninternational.com/seattle",
    rating: 4.5,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 67,
    name: "Pacific Gateway International College",
    city: "Seattle", cityMn: "Сиетл",
    address: "1200 5th Ave Suite 1600, Seattle, WA 98101",
    programs: ["Intensive English", "Academic English", "Business Communication", "IELTS Prep"],
    tuitionPerWeek: 350, tuitionPerMonth: 1320,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Pacific Rim орнуудын оюутнуудтай", "Далайн эрэгт ойр", "IELTS шаардахгүй"],
    website: "pgic.edu",
    rating: 4.3,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 68,
    name: "University of Washington — ELP",
    city: "Seattle", cityMn: "Сиетл",
    address: "4333 Brooklyn Ave NE, Seattle, WA 98105",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 510, tuitionPerMonth: 1940,
    minDurationWeeks: 24,
    accreditation: "CEA, UCIEP",
    highlights: ["Дэлхийд алдартай UW", "Тэтгэлэг боломжтой", "Amazon, Microsoft ойролцоо"],
    website: "uwenglish.washington.edu",
    rating: 4.8,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: false,
  },
  {
    id: 69,
    name: "Seattle University — ELP",
    city: "Seattle", cityMn: "Сиетл",
    address: "901 12th Ave, Seattle, WA 98122",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 480, tuitionPerMonth: 1820,
    minDurationWeeks: 24,
    accreditation: "CEA, NWCCU",
    highlights: ["Jesuit их сургууль", "Тэтгэлэг боломжтой", "Хотын төвд"],
    website: "seattleu.edu/elp",
    rating: 4.6,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 70,
    name: "Washington State University — ELP",
    city: "Seattle", cityMn: "Сиетл",
    address: "PO Box 641048, Pullman, WA 99164",
    programs: ["Intensive English", "Academic English", "TOEFL Prep", "Graduate Prep"],
    tuitionPerWeek: 460, tuitionPerMonth: 1740,
    minDurationWeeks: 24,
    accreditation: "CEA, NWCCU",
    highlights: ["WSU кампус", "Тэтгэлэг боломжтой", "WSU-д шилжих боломж"],
    website: "wsu.edu/elp",
    rating: 4.5,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 71,
    name: "Gonzaga University — ELP",
    city: "Seattle", cityMn: "Сиетл",
    address: "502 E Boone Ave, Spokane, WA 99258",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "Business English"],
    tuitionPerWeek: 440, tuitionPerMonth: 1660,
    minDurationWeeks: 24,
    accreditation: "CEA, NWCCU",
    highlights: ["Jesuit их сургууль", "Тэтгэлэг боломжтой", "Тайван кампус"],
    website: "gonzaga.edu/elp",
    rating: 4.6,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 72,
    name: "Shoreline Community College — ESL",
    city: "Seattle", cityMn: "Сиетл",
    address: "16101 Greenwood Ave N, Shoreline, WA 98133",
    programs: ["ESL Intensive", "Academic English", "Vocational English", "Conversation"],
    tuitionPerWeek: 135, tuitionPerMonth: 520,
    minDurationWeeks: 24,
    accreditation: "NWCCU",
    highlights: ["Хямд үнэ", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Тайван хот"],
    website: "shoreline.edu",
    rating: 4.2,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },

  /* ══════════════════════════════════════════════════════════
     DENVER — 12 schools
  ══════════════════════════════════════════════════════════ */
  {
    id: 73,
    name: "Community College of Denver — ESL",
    city: "Denver", cityMn: "Денвер",
    address: "1111 W Colfax Ave, Denver, CO 80204",
    programs: ["ESL Intensive", "Academic English", "Vocational English", "Conversation"],
    tuitionPerWeek: 120, tuitionPerMonth: 460,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Денверт хамгийн хямд", "Тэтгэлэг боломжтой", "IELTS шаардахгүй"],
    website: "ccd.edu",
    rating: 4.1,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 74,
    name: "Arapahoe Community College — ESL",
    city: "Denver", cityMn: "Денвер",
    address: "5900 S Santa Fe Dr, Littleton, CO 80120",
    programs: ["ESL Foundation", "ESL Intensive", "Academic Preparation", "Vocational English"],
    tuitionPerWeek: 125, tuitionPerMonth: 480,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Хямд үнэ", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Уулын орчин"],
    website: "arapahoe.edu",
    rating: 4.1,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 75,
    name: "Denver Language School",
    city: "Denver", cityMn: "Денвер",
    address: "1600 Glenarm Pl Suite 1100, Denver, CO 80202",
    programs: ["Intensive English", "Academic English", "TOEFL Prep", "Cultural Exchange"],
    tuitionPerWeek: 310, tuitionPerMonth: 1180,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Хамгийн хямд хэлний сургууль", "Уулын орчин", "IELTS шаардахгүй"],
    website: "denverlanguageschool.com",
    rating: 4.2,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 76,
    name: "Colorado English Language Center",
    city: "Denver", cityMn: "Денвер",
    address: "1600 Glenarm Pl, Denver, CO 80202",
    programs: ["Intensive English", "Business English", "IELTS/TOEFL", "Conversation"],
    tuitionPerWeek: 340, tuitionPerMonth: 1280,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Хямд үнэ", "Уулын аялал жуулчлал", "IELTS шаардахгүй"],
    website: "coloradoenglish.com",
    rating: 4.3,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 77,
    name: "Rocky Mountain English Institute",
    city: "Denver", cityMn: "Денвер",
    address: "1700 Lincoln St, Denver, CO 80203",
    programs: ["Intensive English", "Business English", "Ski & English Program", "TOEFL Prep"],
    tuitionPerWeek: 360, tuitionPerMonth: 1360,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Уулын спорт + хэл сурах", "Ski resort-тай хамтын ажиллагаа", "IELTS шаардахгүй"],
    website: "rmei.edu",
    rating: 4.4,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 78,
    name: "Kaplan International Denver",
    city: "Denver", cityMn: "Денвер",
    address: "999 18th St, Denver, CO 80202",
    programs: ["General English", "IELTS Prep", "University Pathway", "Business English"],
    tuitionPerWeek: 395, tuitionPerMonth: 1490,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Хотын төвд", "Уулын спорт боломж", "IELTS шаардахгүй"],
    website: "kaplaninternational.com/denver",
    rating: 4.4,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 79,
    name: "University of Denver — ELC",
    city: "Denver", cityMn: "Денвер",
    address: "2199 S University Blvd, Denver, CO 80208",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 480, tuitionPerMonth: 1820,
    minDurationWeeks: 24,
    accreditation: "CEA, HLC",
    highlights: ["Их сургуулийн кампус", "Тэтгэлэг боломжтой", "DU-д шилжих боломж"],
    website: "du.edu/elc",
    rating: 4.7,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 80,
    name: "Colorado State University — IEP",
    city: "Denver", cityMn: "Денвер",
    address: "1062 Campus Delivery, Fort Collins, CO 80523",
    programs: ["Intensive English", "Academic English", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 460, tuitionPerMonth: 1740,
    minDurationWeeks: 24,
    accreditation: "CEA, HLC",
    highlights: ["CSU кампус", "Тэтгэлэг боломжтой", "Уулын байгалийн орчин"],
    website: "colostate.edu/iep",
    rating: 4.6,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 81,
    name: "Metropolitan State University Denver — ESL",
    city: "Denver", cityMn: "Денвер",
    address: "890 Auraria Pkwy, Denver, CO 80204",
    programs: ["Academic English", "University Prep", "TOEFL/IELTS", "Professional English"],
    tuitionPerWeek: 420, tuitionPerMonth: 1580,
    minDurationWeeks: 24,
    accreditation: "CEA, HLC",
    highlights: ["Их сургуулийн кампус", "Тэтгэлэг боломжтой", "MSU-д шилжих боломж"],
    website: "msudenver.edu/esl",
    rating: 4.5,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 82,
    name: "Regis University — ELP",
    city: "Denver", cityMn: "Денвер",
    address: "3333 Regis Blvd, Denver, CO 80221",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "Business English"],
    tuitionPerWeek: 440, tuitionPerMonth: 1660,
    minDurationWeeks: 24,
    accreditation: "CEA, HLC",
    highlights: ["Jesuit их сургууль", "Тэтгэлэг боломжтой", "Тайван кампус"],
    website: "regis.edu/elp",
    rating: 4.5,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 83,
    name: "Front Range Community College — ESL",
    city: "Denver", cityMn: "Денвер",
    address: "4616 S Shields St, Fort Collins, CO 80526",
    programs: ["ESL Intensive", "Academic English", "Vocational English", "Conversation"],
    tuitionPerWeek: 115, tuitionPerMonth: 445,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Маш хямд", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Уулын орчин"],
    website: "frontrange.edu",
    rating: 4.0,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 84,
    name: "Red Rocks Community College — ESL",
    city: "Denver", cityMn: "Денвер",
    address: "13300 W 6th Ave, Lakewood, CO 80228",
    programs: ["ESL Foundation", "ESL Intensive", "Academic Preparation", "Vocational English"],
    tuitionPerWeek: 120, tuitionPerMonth: 460,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Red Rocks ойролцоо", "Тэтгэлэг боломжтой", "IELTS шаардахгүй"],
    website: "rrcc.edu",
    rating: 4.1,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },

  /* ══════════════════════════════════════════════════════════
     KANSAS CITY — 12 schools
  ══════════════════════════════════════════════════════════ */
  {
    id: 85,
    name: "Avila University — ESL Program",
    city: "Kansas City", cityMn: "Канзас Сити",
    address: "11901 Wornall Rd, Kansas City, MO 64145",
    programs: ["ESL Intensive", "Academic English", "University Pathway", "Business English"],
    tuitionPerWeek: 280, tuitionPerMonth: 1060,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Авила их сургуульд шилжих боломж", "Хямд амьдралын зардал"],
    website: "avila.edu/esl",
    rating: 4.4,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 86,
    name: "Kansas City Kansas Community College — ESL",
    city: "Kansas City", cityMn: "Канзас Сити",
    address: "7250 State Ave, Kansas City, KS 66112",
    programs: ["ESL Foundation", "ESL Intensive", "Academic Preparation", "Vocational English"],
    tuitionPerWeek: 100, tuitionPerMonth: 390,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Хамгийн хямд", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Амьдралын зардал бага"],
    website: "kckcc.edu",
    rating: 4.0,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 87,
    name: "Metropolitan Community College KC — ESL",
    city: "Kansas City", cityMn: "Канзас Сити",
    address: "3200 Broadway Blvd, Kansas City, MO 64111",
    programs: ["ESL Intensive", "Academic English", "Business English", "Conversation"],
    tuitionPerWeek: 110, tuitionPerMonth: 420,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Хямд үнэ", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Хотын төвд"],
    website: "mcckc.edu",
    rating: 4.1,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 88,
    name: "Johnson County Community College — ESL",
    city: "Kansas City", cityMn: "Канзас Сити",
    address: "12345 College Blvd, Overland Park, KS 66210",
    programs: ["ESL Foundation", "ESL Intensive", "Academic Preparation", "Vocational English"],
    tuitionPerWeek: 115, tuitionPerMonth: 445,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Аюулгүй хот", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Хямд амьдрал"],
    website: "jccc.edu",
    rating: 4.2,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 89,
    name: "Donnelly College — ESL",
    city: "Kansas City", cityMn: "Канзас Сити",
    address: "608 N 18th St, Kansas City, KS 66102",
    programs: ["ESL Intensive", "Academic English", "Vocational English", "Conversation"],
    tuitionPerWeek: 120, tuitionPerMonth: 460,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Хямд үнэ", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Олон улсын орчин"],
    website: "donnelly.edu",
    rating: 4.1,
    schoolType: "community_college",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 90,
    name: "Kansas City Language Institute",
    city: "Kansas City", cityMn: "Канзас Сити",
    address: "1000 Walnut St, Kansas City, MO 64106",
    programs: ["General English", "Intensive English", "Business English", "TOEFL Prep"],
    tuitionPerWeek: 260, tuitionPerMonth: 980,
    minDurationWeeks: 4,
    accreditation: "ACCET",
    highlights: ["Хямд үнэ", "IELTS шаардахгүй", "Уян хатан хуваарь", "Хотын төвд"],
    website: "kcli.edu",
    rating: 4.2,
    schoolType: "language_school",
    hasScholarship: false,
    noEnglishTest: true,
  },
  {
    id: 91,
    name: "University of Missouri-Kansas City — ELP",
    city: "Kansas City", cityMn: "Канзас Сити",
    address: "5100 Rockhill Rd, Kansas City, MO 64110",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 380, tuitionPerMonth: 1440,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Их сургуулийн кампус", "Тэтгэлэг боломжтой", "UMKC-д шилжих боломж"],
    website: "umkc.edu/elp",
    rating: 4.5,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 92,
    name: "Rockhurst University — ELP",
    city: "Kansas City", cityMn: "Канзас Сити",
    address: "1100 Rockhurst Rd, Kansas City, MO 64110",
    programs: ["Intensive English", "Academic English", "Business English", "TOEFL Prep"],
    tuitionPerWeek: 350, tuitionPerMonth: 1320,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Jesuit их сургууль", "Тэтгэлэг боломжтой", "Тайван кампус"],
    website: "rockhurst.edu/elp",
    rating: 4.4,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 93,
    name: "William Jewell College — ELP",
    city: "Kansas City", cityMn: "Канзас Сити",
    address: "500 College Hill, Liberty, MO 64068",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "Liberal Arts English"],
    tuitionPerWeek: 320, tuitionPerMonth: 1220,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Жижиг, тайван кампус", "Тэтгэлэг боломжтой", "IELTS шаардахгүй"],
    website: "jewell.edu/elp",
    rating: 4.3,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 94,
    name: "Benedictine College — ELP",
    city: "Kansas City", cityMn: "Канзас Сити",
    address: "1020 N 2nd St, Atchison, KS 66002",
    programs: ["Intensive English", "Academic English", "TOEFL Prep", "University Pathway"],
    tuitionPerWeek: 300, tuitionPerMonth: 1140,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Католик их сургууль", "Тэтгэлэг боломжтой", "IELTS шаардахгүй"],
    website: "benedictine.edu/elp",
    rating: 4.3,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 95,
    name: "Park University — ELP",
    city: "Kansas City", cityMn: "Канзас Сити",
    address: "8700 NW River Park Dr, Parkville, MO 64152",
    programs: ["Intensive English", "Academic Preparation", "TOEFL Prep", "Business English"],
    tuitionPerWeek: 290, tuitionPerMonth: 1100,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Хямд их сургууль", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Голын эрэгт"],
    website: "park.edu/elp",
    rating: 4.2,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
  {
    id: 96,
    name: "MidAmerica Nazarene University — ELP",
    city: "Kansas City", cityMn: "Канзас Сити",
    address: "2030 E College Way, Olathe, KS 66062",
    programs: ["Intensive English", "Academic English", "TOEFL Prep", "Christian Liberal Arts"],
    tuitionPerWeek: 270, tuitionPerMonth: 1020,
    minDurationWeeks: 24,
    accreditation: "HLC",
    highlights: ["Хямд үнэ", "Тэтгэлэг боломжтой", "IELTS шаардахгүй", "Аюулгүй хот"],
    website: "mnu.edu/elp",
    rating: 4.1,
    schoolType: "university_eli",
    hasScholarship: true,
    noEnglishTest: true,
  },
];

export const CITIES = [
  "Бүгд", "Чикаго", "Лос-Анжелес", "Нью-Йорк", "Бостон",
  "Вашингтон", "Сиетл", "Денвер", "Канзас Сити",
] as const;
export type CityFilter = typeof CITIES[number];

export const SCHOOL_TYPE_LABELS: Record<SchoolType, string> = {
  community_college: "Community College",
  language_school: "Хэлний сургууль",
  university_eli: "Их сургуулийн ELI",
};
